/**
 * This tab's copy of the help-assistant conversation (#3624): sessionStorage,
 * keyed by the signed-in user's id, so a page reload does not lose the chat.
 * Removed by "New chat", on sign-out and on session expiry (the auth context
 * calls ``clearStoredChats``), and never read for a different user. A reply
 * still streaming when the page went away is restored as finished, or as an
 * error when it had no text. Best-effort throughout: private windows, blocked
 * storage or a full quota simply leave the chat in memory.
 */
import type { ChatMessage } from "../../contexts/supportChat";

export const STORAGE_PREFIX = "support-chat:v1:";
const MAX_STORED_MESSAGES = 60;
const LOST_REPLY = "Sorry, that reply was interrupted. Please ask again.";

export interface StoredChat {
  conversationId: number | null;
  messages: ChatMessage[];
}

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function storageKey(userId: number | string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

/** Drop every stored conversation except (optionally) this user's. */
export function clearStoredChats(keepUserId: number | string | null = null): void {
  const store = storage();
  if (!store) return;
  try {
    const keep = keepUserId === null ? null : storageKey(keepUserId);
    const keys: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && key.startsWith(STORAGE_PREFIX) && key !== keep) keys.push(key);
    }
    keys.forEach((k) => store.removeItem(k));
  } catch {
    /* storage unavailable: nothing to clear */
  }
}

function isStoredMessage(m: unknown): m is ChatMessage {
  if (!m || typeof m !== "object") return false;
  const msg = m as Record<string, unknown>;
  return (
    typeof msg.id === "string" &&
    (msg.role === "user" || msg.role === "assistant") &&
    typeof msg.text === "string"
  );
}

export function loadStoredChat(userId: number | string): StoredChat | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(storageKey(userId));
    if (!raw) return null;
    const data = JSON.parse(raw) as Partial<StoredChat>;
    const messages = Array.isArray(data.messages) ? data.messages.filter(isStoredMessage) : [];
    if (messages.length === 0) return null;
    return {
      conversationId: typeof data.conversationId === "number" ? data.conversationId : null,
      messages: messages.map((m) =>
        m.status === "streaming"
          ? { ...m, status: m.text ? "done" : "error", text: m.text || LOST_REPLY }
          : m,
      ),
    };
  } catch {
    return null;
  }
}

export function saveStoredChat(userId: number | string, chat: StoredChat): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(
      storageKey(userId),
      JSON.stringify({ ...chat, messages: chat.messages.slice(-MAX_STORED_MESSAGES) }),
    );
  } catch {
    /* quota or privacy mode: the chat stays in memory only */
  }
}

/** Remove this user's stored conversation ("New chat"). */
export function removeStoredChat(userId: number | string): void {
  try {
    storage()?.removeItem(storageKey(userId));
  } catch {
    /* nothing stored */
  }
}
